import { get as lodashGet } from "lodash";
import React, { type ComponentPropsWithoutRef } from "react";
import PillarTableColumnChild from "shared/components/pillar-table/cell-content/PillarTableColumnChild";
import {
  type UnwrapArray,
  type DeepValue,
  type PathToArray,
  pillarTableColumnArrayValue,
} from "shared/components/pillar-table/columns/array/PillarTableColumnArray";
import { type PillarTableBaseColumnCellValuePathProps } from "shared/components/pillar-table/columns/PillarTableColumn";
import { type PillarTableSafeAny } from "shared/components/pillar-table/PillarTableSafeAny";
import { usePillarTableRowContext } from "shared/components/pillar-table/row/PillarTableRowContext";
import { type DeepKeys } from "shared/types/deepkeys";

type PillarTableColumnArrayFindProps<T> = {
  dataType?: "string" | "number" | PillarTableSafeAny;
  // findPath must be a valid deep key relative to the element type of the array at `cellValuePath`.
  findPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  outputPath?: DeepKeys<UnwrapArray<DeepValue<T, PathToArray<T>>>>;
  /**
   * When using an array of strings or numbers for findValue, the column will match the first matching value.
   */
  findValue: string | number | string[] | number[];
  matchOutput?: string;
  noMatchOutput?: string;
  children?: React.ReactNode;
} & Omit<PillarTableBaseColumnCellValuePathProps<T>, "cellValuePath"> & {
    cellValuePath?: PathToArray<T>;
  } & ComponentPropsWithoutRef<"div">;

export const PillarTableColumnArrayFind = <T,>({
  cellValuePath,
  children,
  dataType,
  findPath,
  findValue,
  outputPath,
  matchOutput,
  noMatchOutput,
}: PillarTableColumnArrayFindProps<T>) => {
  const rowContext = usePillarTableRowContext();
  const arrayValue = pillarTableColumnArrayValue({
    rowContext,
    cellValuePath,
  });
  const foundArrayElement = arrayValue?.find(
    (item: DeepValue<T, PathToArray<T>>) => {
      if (!findPath) {
        if (typeof item !== "string" && typeof item !== "number") {
          return false;
        }

        return Array.isArray(findValue)
          ? findValue.some((val) => val === item)
          : item === findValue;
      }

      return Array.isArray(findValue)
        ? findValue.some((val) => lodashGet(item, findPath) === val)
        : lodashGet(item, findPath) === findValue;
    },
  );

  const valuePath = outputPath ?? findPath;

  const outputValue = !findPath
    ? foundArrayElement
    : lodashGet(foundArrayElement, valuePath ?? "");

  if (matchOutput && outputValue) {
    return <>{matchOutput}</>;
  }
  if (noMatchOutput && !outputValue) {
    return <>{noMatchOutput}</>;
  }
  if (
    (dataType === "number" && typeof outputValue === "number") ||
    (dataType === "string" && typeof outputValue === "string")
  ) {
    return <>{outputValue}</>;
  }
  return (
    <PillarTableColumnChild
      rowValueOverride={foundArrayElement}
      rowIndex={rowContext?.rowIndex}
      cellValuePath={valuePath}
      columnConfig={dataType}
    >
      {children}
    </PillarTableColumnChild>
  );
};
